import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { FIPSState } from '../entities/FIPSState';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { EqualFilter, InFilter } from '../baseServices/filter.interface';
import { Check, EntityHelper } from '../utils/entity-helper';

@Injectable({
  providedIn: 'root',
})
export class FIPSStateService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<FIPSState>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(fipsState: FIPSState, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: fipsState, who: who, what: what });
  }

  getEmitChangeCallback(fipsState: FIPSState, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForFIPSState + '_' + fipsState.RecordId,
      fn: this.emitChange.bind(this, fipsState, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.FIPSState], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getFIPSStateByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FIPSState> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<FIPSState>(StoreName.FIPSState, onlineId, FIPSState, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as FIPSState;

    if (this.checkIsFIPSStateSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getFIPSStateByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FIPSState> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<FIPSState>(StoreName.FIPSState, recordId, FIPSState, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as FIPSState;

    if (this.checkIsFIPSStateSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllFIPSStates(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FIPSState[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<FIPSState>(StoreName.FIPSState, filter, FIPSState, transaction);

    let records = result as FIPSState[];

    if (!includeDeleted) {
      records = records.filter(statusLookup => !this.checkIsFIPSStateSoftDeleted(statusLookup));
    }

    return records;
  }

  async getFIPSStatesByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FIPSState[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<FIPSState>(StoreName.FIPSState, filter, FIPSState, transaction);

    let records = result as FIPSState[];

    if (!includeDeleted) {
      records = result.filter(statusLookup => !this.checkIsFIPSStateSoftDeleted(statusLookup));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createFIPSState(who: DataChangeEmitWho, recordToCreate: FIPSState, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateFIPSStateCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.FIPSState, transaction);

    // LC5. Get created record
    const createdRecord = await this.getFIPSStateByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateFIPSState(who: DataChangeEmitWho, recordToUpdate: FIPSState, skipLocalCopyValidation: boolean, localCopy: FIPSState | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getFIPSStateByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateFIPSStateUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<FIPSState>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<FIPSState>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateFIPSState(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.FIPSState, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getFIPSStateByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateFIPSState(fipsState: FIPSState, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateFIPSStateCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateFIPSStateUpdate(fipsState: FIPSState, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertFIPSStateFromSync(who: DataChangeEmitWho, recordToUpsert: FIPSState, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getFIPSStateByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateFIPSState(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createFIPSState(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsFIPSStateSoftDeleted(record: FIPSState): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: FIPSState, b: FIPSState): boolean {
    return a.Name === b.Name && a.Number === b.Number && a.ENS === b.ENS && a.RequiresRestBreak === b.RequiresRestBreak;
  }

  // #endregion
  // region Lookup

  async getFIPSStateNumberById(id: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const record = await this.getFIPSStateByOnlineId(id, transaction, includeDeleted);

  return record.Number;
  }

  async getFIPSStateNumberByNumber(number: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filterData: EqualFilter = {
      equals: {
        field: 'Number',
        searchString: number,
        caseInsensitive: true,
      },
    };

    const result = await this.getAllFIPSStates(filterData, transaction, includeDeleted);

    if (result.length > 0) {
      const record = result[0];

      if (this.checkIsFIPSStateSoftDeleted(record) && !includeDeleted) {
        throw new RecordSoftDeletedException(number);
      }

      return record.Number;
    } else {
      throw new RecordNotFoundException(number);
    }
  }

  async getFIPSStateNumberByName(recordName: string, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filterData: EqualFilter = {
      equals: {
        field: 'Name',
        searchString: recordName,
        caseInsensitive: true,
      },
    };

    const result = await this.getAllFIPSStates(filterData, transaction, includeDeleted);

    if (result.length > 0) {
      const record = result[0];

      if (this.checkIsFIPSStateSoftDeleted(record) && !includeDeleted) {
        throw new RecordSoftDeletedException(recordName);
      }

      return record.Name;
    } else {
      throw new RecordNotFoundException(recordName);
    }
  }

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
